import React, { useRef, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useActiveMeeting } from "../context/ActiveMeetingContext";
import { useAuth } from "../context/AuthContext";
import {
  FaVideo, FaCopy, FaCheck, FaExpand, FaCompress, FaArrowsAlt,
} from "react-icons/fa";
import { MdCallEnd, MdFlip } from "react-icons/md";
import toast from "react-hot-toast";
import styles from "./JitsiPortal.module.css";

const JAAS_APP_ID = "vpaas-magic-cookie-eb75c557fb044d4180c2a1b4a36659e7";

const portalMeetLink = (room) =>
  `${window.location.origin}/dashboard/meet?room=${encodeURIComponent(room)}`;

/**
 * JitsiPortal — persists the Jitsi meeting iframe across navigation.
 *
 * Rendered inside DashboardLayout, mounts to document.body via createPortal.
 * The Jitsi container div (<div ref={containerRef}) is always at the same
 * position in the JSX tree so React never recreates the DOM node — only CSS
 * classes change to switch between full-screen (on /dashboard/meet) and
 * Picture-in-Picture (any other route).
 */
const JitsiPortal = () => {
  const { activeMeeting, jitsiApiRef, clearMeeting } = useActiveMeeting();
  const { userData } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const containerRef  = useRef(null);  // Jitsi parentNode — never moves in DOM
  const portalRootRef = useRef(null);  // used for requestFullscreen
  const dragState     = useRef(null);  // tracks pointer drag session

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [barCopied, setBarCopied]       = useState(false);
  // Drag position — null means "use CSS default (bottom-right)"
  const [pipPos, setPipPos] = useState(null); // { x, y } in px from top-left
  // Measured navbar height — avoids sub-pixel gaps from CSS variable rounding
  const [navbarH, setNavbarH] = useState(0);

  useEffect(() => {
    const measure = () => {
      const nav = document.querySelector('.navbar');
      if (nav) setNavbarH(nav.getBoundingClientRect().height);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const displayName = userData?.name || userData?.email?.split("@")[0] || "Employee";

  const onMeetPage = location.pathname === "/dashboard/meet";
  const isFull = !!activeMeeting && onMeetPage;
  const isPip  = !!activeMeeting && !onMeetPage;

  /* ── Load JaaS External API script once on portal mount ─────────────── */
  useEffect(() => {
    const existing = document.getElementById("jitsi-ext-api");
    if (existing) {
      if (existing.src?.includes("meet.jit.si")) {
        existing.remove(); // replace stale script from old domain
      } else {
        return; // already loaded
      }
    }
    const s = document.createElement("script");
    s.id    = "jitsi-ext-api";
    s.src   = "https://8x8.vc/libs/external_api.min.js";
    s.async = true;
    document.head.appendChild(s);
  }, []);

  /* ── Initialize / re-initialize Jitsi when activeMeeting changes ────── */
  useEffect(() => {
    if (!activeMeeting) {
      // Meeting was cleared externally — dispose API if still alive
      if (jitsiApiRef.current) {
        try { jitsiApiRef.current.dispose(); } catch (_) {}
        jitsiApiRef.current = null;
      }
      return;
    }

    const init = () => {
      if (!window.JitsiMeetExternalAPI) { setTimeout(init, 150); return; }
      const container = containerRef.current;
      if (!container) { setTimeout(init, 100); return; }

      // Dispose any stale instance before creating a new one
      if (jitsiApiRef.current) {
        try { jitsiApiRef.current.dispose(); } catch (_) {}
      }

      const jaasRoom = `${JAAS_APP_ID}/${activeMeeting.room}`;

      const api = new window.JitsiMeetExternalAPI("8x8.vc", {
        roomName: jaasRoom,
        width:    "100%",
        height:   "100%",
        parentNode: container,
        jwt: activeMeeting.token,
        userInfo: {
          displayName,
          email: userData?.email || "",
        },
        configOverwrite: {
          prejoinPageEnabled: true,
          prejoinConfig: { enabled: true },
          lobby: { enabled: !!activeMeeting.lobbyEnabled },
          disableDeepLinking:            true,
          startWithAudioMuted:           false,
          startWithVideoMuted:           false,
          subject:                       "PrrepX Meet",
          enableClosePage:               false,
          enableInsecureRoomNameWarning: false,
          // Default to tile/grid view so all participants are visible
          startInTileView:               true,
          // Only one screen share allowed at a time (like Zoom/GMeet)
          enableMultipleParticipantsShareScreen: false,
          // Disable 1-on-1 mode so tile view works even with 2 participants
          disable1On1Mode:               true,
          // Hide the tile-view toggle — tile view is always on by default
          toolbarButtons: [
            'microphone', 'camera', 'desktop', 'chat', 'raisehand',
            'participants-pane', 'hangup', 'fullscreen', 'invite',
            'settings', 'videoquality', 'filmstrip', 'shareaudio',
            'select-background', 'recording', 'security',
            'mute-everyone', 'mute-video-everyone', 'toggle-camera',
            'whiteboard', 'closedcaptions', 'sharedvideo',
            'noisesuppression', 'profile', 'shortcuts', 'stats',
            'help', 'highlight', 'etherpad',
          ],
        },
        interfaceConfigOverwrite: {
          SHOW_JITSI_WATERMARK:         false,
          SHOW_WATERMARK_FOR_GUESTS:    false,
          APP_NAME:                     "PrrepX Meet",
          NATIVE_APP_NAME:              "PrrepX Meet",
          PROVIDER_NAME:                "PrrepX",
          SHOW_BRAND_WATERMARK:         false,
          SHOW_POWERED_BY:              false,
          SHOW_PROMOTIONAL_CLOSE_PAGE:  false,
          DEFAULT_REMOTE_DISPLAY_NAME:  "Teammate",
          DEFAULT_LOCAL_DISPLAY_NAME:   "Me",
        },
      });

      // Enable cam/mic inside cross-origin iframe (required on mobile)
      const iframe = api.getIFrame();
      if (iframe) {
        iframe.setAttribute(
          "allow",
          "camera; microphone; display-capture; fullscreen; autoplay; clipboard-write"
        );
      }

      // Force tile view on join and when participants join.
      const forceTileView = () => {
        try { api.executeCommand('setTileView', true); } catch (_) {}
      };

      api.addEventListener('videoConferenceJoined', () => {
        forceTileView();
        // Show hint about Flip Video button (only once per session)
        if (!sessionStorage.getItem('prrepx-flip-hint-shown')) {
          sessionStorage.setItem('prrepx-flip-hint-shown', '1');
          toast('Video mirrored? Click "Flip Video" button above to fix it.', {
            icon: '🔄',
            duration: 6000,
            style: { background: '#1e293b', color: '#e2e8f0', fontSize: '0.85rem' },
          });
        }
      });
      api.addEventListener('participantJoined', forceTileView);

      // Jitsi-initiated leave (user presses Leave inside Jitsi UI)
      const doLeave = () => {
        const exec = () => {
          clearMeeting();
          // Clean up the ?room= query param if the user is currently on the meet page
          if (window.location.pathname === "/dashboard/meet") {
            navigate("/dashboard/meet", { replace: true });
          }
        };
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(() => {}).finally(exec);
        } else {
          exec();
        }
      };

      api.addEventListener("videoConferenceLeft", doLeave);
      api.addEventListener("readyToClose",        doLeave);
      jitsiApiRef.current = api;
    };

    init();

    // NOTE: no cleanup return — the meeting must survive re-renders.
    // Disposal happens only when activeMeeting becomes null (handled above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMeeting]);

  /* ── Fullscreen support ─────────────────────────────────────────────── */
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      portalRootRef.current?.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  };
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  /* ── User-initiated leave ────────────────────────────────────────────── */
  const handleLeave = () => {
    const exec = () => {
      clearMeeting();
      // If leaving from full-screen meet page, clear the URL query params
      if (isFull) navigate("/dashboard/meet", { replace: true });
    };
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {}).finally(exec);
    } else {
      exec();
    }
  };

  /* ── PiP: expand back to full meeting page ───────────────────────────── */
  const handleExpandPip = () => navigate("/dashboard/meet");

  /* ── PiP drag (pointer events — works for mouse + touch) ─────────────── */
  const handleDragStart = (e) => {
    if (!isPip) return;
    // Don't steal clicks on the action buttons
    if (e.target.closest('button') || e.target.closest('a')) return;
    e.preventDefault();
    const el = portalRootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    dragState.current = {
      startX: e.clientX - rect.left,
      startY: e.clientY - rect.top,
    };
    el.setPointerCapture(e.pointerId);
  };

  const handleDragMove = (e) => {
    if (!dragState.current) return;
    const el = portalRootRef.current;
    if (!el) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w  = el.offsetWidth;
    const h  = el.offsetHeight;
    // Clamp so it can't be dragged fully off-screen
    const x = Math.max(0, Math.min(vw - w, e.clientX - dragState.current.startX));
    const y = Math.max(0, Math.min(vh - h, e.clientY - dragState.current.startY));
    setPipPos({ x, y });
  };

  const handleDragEnd = () => { dragState.current = null; };

  // Reset to default corner position when PiP first appears
  useEffect(() => {
    if (!isPip) setPipPos(null);
  }, [isPip]);

  /* ── Flip / un-mirror local video ───────────────────────────────────── */
  const handleFlipVideo = () => {
    if (jitsiApiRef.current) {
      try { jitsiApiRef.current.executeCommand('toggleCameraMirror'); } catch (_) {}
    }
  };

  /* ── Copy invite link ───────────────────────────────────────────────── */
  const meetLink = activeMeeting ? portalMeetLink(activeMeeting.room) : "";
  const handleCopy = () => {
    navigator.clipboard.writeText(meetLink).then(() => {
      setBarCopied(true);
      setTimeout(() => setBarCopied(false), 2000);
    });
  };

  /* ── No meeting → render nothing ────────────────────────────────────── */
  if (!activeMeeting) return null;

  // Build inline style for dragged PiP — override CSS bottom/right with top/left
  const pipStyle = (isPip && pipPos)
    ? { top: pipPos.y, left: pipPos.x, bottom: 'auto', right: 'auto' }
    : undefined;

  // For full mode, override `top` with the precisely measured navbar height
  const fullStyle = (isFull && navbarH > 0) ? { top: navbarH } : undefined;

  return ReactDOM.createPortal(
    <div
      ref={portalRootRef}
      className={`${styles.portalRoot} ${isFull ? styles.portalFull : styles.portalPip}`}
      style={isFull ? fullStyle : pipStyle}
      onPointerMove={isPip ? handleDragMove : undefined}
      onPointerUp={isPip ? handleDragEnd : undefined}
      onPointerCancel={isPip ? handleDragEnd : undefined}
    >
      {/* ── TopBar (full-screen mode only) ────────────────────────────── */}
      {isFull && (
        <div className={styles.topBar}>
          <div className={styles.topBarLeft}>
            <div className={styles.liveDot} />
            <span className={styles.liveLabel}>LIVE</span>
            <span className={styles.topBarRoom}>{activeMeeting.room}</span>
          </div>
          <div className={styles.topBarActions}>
            <button
              className={styles.topBarBtn}
              onClick={handleCopy}
              title="Copy invite link"
            >
              {barCopied ? <FaCheck size={13} /> : <FaCopy size={13} />}
              <span>{barCopied ? "Copied!" : "Copy Link"}</span>
            </button>
            <button
              className={styles.topBarBtn}
              onClick={handleFlipVideo}
              title="Fix mirrored video"
            >
              <MdFlip size={15} />
              <span>Flip Video</span>
            </button>
            <button
              className={styles.topBarBtn}
              onClick={toggleFullscreen}
              title={isFullscreen ? "Exit fullscreen" : "Full screen"}
            >
              {isFullscreen ? <FaCompress size={13} /> : <FaExpand size={13} />}
              <span>{isFullscreen ? "Exit Full" : "Full Screen"}</span>
            </button>
          </div>
        </div>
      )}

      {/* ── Jitsi container — always at same position in JSX tree ──────
           React reuses this exact DOM node when toggling between full/pip,
           so the iframe connection is never broken.                       */}
      <div ref={containerRef} className={styles.jitsiFrame} />


      {/* ── PiP controls overlay ─────────────────────────────────────── */}
      {isPip && (
        <div className={styles.pipOverlay}>
          {/* Top strip: drag handle + room name */}
          <div className={styles.pipTopBar} title="Drag to move"
            onPointerDown={handleDragStart}
          >
            <span className={styles.pipDragHandle}>
              <FaArrowsAlt size={11} />
            </span>
            <span className={styles.pipLiveDot} />
            <span className={styles.pipRoom}>{activeMeeting.room}</span>
          </div>
          <div className={styles.pipBtnRow}>
            <button
              className={styles.pipBtn}
              onClick={handleExpandPip}
              title="Return to meeting"
            >
              <FaExpand size={11} />
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default JitsiPortal;
